import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Custom gallery component for Estro product pages.
 * Uses Swiper for smooth looping carousel.
 * Layout: 2 large images on top + 3 thumbnails with arrows below.
 * Both sliders are linked via Swiper controller.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} topSwiper - Top swiper container
 * @property {HTMLElement} thumbSwiper - Thumbs swiper container
 *
 * @extends Component<Refs>
 */
export class EstroGallery extends Component {
  /** @type {any} */
  #topSwiperInstance = null;

  /** @type {any} */
  #thumbSwiperInstance = null;

  /** @type {boolean} */
  #syncing = false;

  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, /** @type {EventListener} */ (this.#handleVariantUpdate), { signal });

    // Handle zoom clicks via delegation (works for Swiper loop clones too)
    this.#initZoomClickHandler(signal);

    // Swiper may not be loaded yet (deferred script), wait for it
    if (/** @type {any} */ (window).Swiper) {
      requestAnimationFrame(() => this.#initSwipers());
    } else {
      const script = document.querySelector('script[src*="swiper-bundle"]');
      if (script) {
        script.addEventListener('load', () => requestAnimationFrame(() => this.#initSwipers()), { signal });
      }
      // Fallback: if we missed the load event, try on DOMContentLoaded
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          if (!this.#topSwiperInstance && /** @type {any} */ (window).Swiper) {
            requestAnimationFrame(() => this.#initSwipers());
          }
        }, { signal });
      } else if (!this.#topSwiperInstance) {
        // Document already loaded, try init in next frame
        requestAnimationFrame(() => {
          if (!this.#topSwiperInstance && /** @type {any} */ (window).Swiper) {
            this.#initSwipers();
          }
        });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#topSwiperInstance?.destroy(true, true);
    this.#thumbSwiperInstance?.destroy(true, true);
  }

  #initZoomClickHandler(signal) {
    const zoomDialogId = this.dataset.zoomDialogId;
    if (!zoomDialogId) return;

    // Bind on the whole gallery so both the desktop layout and the mobile
    // carousel open the lightbox on click/tap.
    this.addEventListener('click', (event) => {
      const slide = /** @type {HTMLElement} */ (event.target)?.closest('.product-media-container--zoomable');
      if (!slide) return;

      const mediaIndex = slide.getAttribute('data-media-index');
      if (mediaIndex == null) return;

      const zoomDialog = /** @type {any} */ (document.getElementById(zoomDialogId));
      if (!zoomDialog?.open) return;

      zoomDialog.open(parseInt(mediaIndex, 10), event);
    }, { signal });
  }

  #initSwipers() {
    // @ts-ignore — Swiper loaded as global from swiper-bundle.min.js
    const SwiperClass = /** @type {any} */ (window).Swiper;
    if (!SwiperClass) return;

    const topEl = this.refs.topSwiper;
    const thumbEl = this.refs.thumbSwiper;
    if (!topEl || !thumbEl) return;

    const gap = parseInt(getComputedStyle(this).getPropertyValue('--image-gap')) || 8;

    this.#topSwiperInstance = new SwiperClass(topEl, {
      slidesPerView: 2,
      spaceBetween: gap,
      loop: true,
      loopedSlides: 2,
      allowTouchMove: false,
    });

    this.#thumbSwiperInstance = new SwiperClass(thumbEl, {
      slidesPerView: 3,
      spaceBetween: gap,
      loop: true,
      loopedSlides: 2,
      allowTouchMove: false,
      navigation: {
        nextEl: this.querySelector('.estro-gallery__arrow--next'),
        prevEl: this.querySelector('.estro-gallery__arrow--prev'),
      },
    });

    // Manually sync: when thumbs move, move top slider the same direction.
    // This avoids Swiper controller.control desync with different slidesPerView + loop.
    this.#thumbSwiperInstance.on('slideNextTransitionStart', () => {
      if (this.#syncing) return;
      this.#syncing = true;
      this.#topSwiperInstance?.slideNext(this.#thumbSwiperInstance.params.speed);
      this.#syncing = false;
    });

    this.#thumbSwiperInstance.on('slidePrevTransitionStart', () => {
      if (this.#syncing) return;
      this.#syncing = true;
      this.#topSwiperInstance?.slidePrev(this.#thumbSwiperInstance.params.speed);
      this.#syncing = false;
    });
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const newGallery = source.querySelector('estro-gallery');
    if (!newGallery) return;

    // Compare media IDs — if the images are the same (e.g. size change), skip replacement to preserve Swiper state
    const currentMediaIds = this.#getMediaIds(this);
    const newMediaIds = this.#getMediaIds(newGallery);

    if (currentMediaIds === newMediaIds) return;

    this.replaceWith(newGallery);
  };

  /**
   * Extracts a string of media IDs from gallery for comparison.
   * @param {HTMLElement} gallery
   * @returns {string}
   */
  #getMediaIds(gallery) {
    const mediaElements = gallery.querySelectorAll('.estro-gallery__desktop [data-media-id]');
    return Array.from(mediaElements)
      .map((el) => el.getAttribute('data-media-id'))
      .join(',');
  }
}

if (!customElements.get('estro-gallery')) {
  customElements.define('estro-gallery', EstroGallery);
}
